import React, { useState, useEffect } from "react";
import { getSupabase } from "./supabaseClient";
import "../css/RecentAudios.css";
import {
  HStack,
  Input,
  Button,
  Flex,
  Box,
  Text,
  Center,
  Select,
} from "@chakra-ui/react";
import {
  bucketName_files,
  tableName,
  data_column,
  prompts,
  file_reference_column,
  supabaseUrl,
  defaultItemsPerPage,
  getBackendURL,
} from "./env.js";
import jsonToFormattedText from "./JSONToMarkdown.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Modal from "react-modal";
import ResultsTable from "./table/ResultsTable.js";
import Uploader from "./upload/Uploader.js";
import {
  handleViewAudioFiles,
  sortObjectsByDateTime,
  sortObjectsByTime,
} from "../utils/utils.js";
import { isInGroup } from "../config/roles";
import { sharedUsersOverlapOrClauses } from "../utils/shareAccess.js";
import {
  hydrateScheduleRowsTimezoneSource,
  fetchViewerIanaTimezone,
} from "../utils/scheduleRowTimezone.js";

const RecentAudios = ({ newAudioCreated }) => {
  const [allRecentAudios, setAllRecentAudios] = useState(undefined);
  const [recentAudios, setRecentAudios] = useState([]);
  const [currentFolderPath, setCurrentFolderPath] = useState("");
  const [previousFolderPath, setPreviousFolderPath] = useState("");
  const [showAudios, setShowAudios] = useState(false);
  const [currentFolderFiles, setCurrentFolderFiles] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);

  // Select checkbox
  const [buttonTexts, setButtonTexts] = useState([]);
  const [selectedRows, setSelectedRows] = useState({});
  const [viewerTimezone, setViewerTimezone] = useState("America/New_York");

  useEffect(() => {
    (async () => {
      const supabase = await getSupabase();
      setViewerTimezone(await fetchViewerIanaTimezone(supabase));
    })();
  }, []);

  const getRecentAudios = async (page = 1) => {
    const supabase = await getSupabase();
    try {
      const { data: sessionData, error: sessionError } =
        await supabase.auth.getSession();
      if (sessionError) {
        console.error("Error getting session:", sessionError);
        return;
      }

      const userId = sessionData?.session?.user?.id;
      if (!userId) {
        console.error(
          "User ID not found in session. Cannot proceed with upload.",
        );
        return;
      }

      console.log("User ID:", userId);

      const { data: userRows, error: userErr } = await supabase
        .from("users")
        .select("role")
        .eq("email", sessionData?.session?.user?.email);

      if (userErr || !userRows || userRows.length === 0) {
        console.error("Error fetching user role:", userErr);
        return;
      }

      const role = userRows[0].role;
      console.log(role);

      let query = supabase.from(tableName).select("*", { count: "exact" });
      if (role !== "admin" && role !== "doctor") {
        if (isInGroup(role, 'tenantSchema')) {
          let ownerIds = [];
          try {
            const backendUrl = getBackendURL();
            const res = await fetch(
              `${backendUrl}/api/shared-all-owner-ids/?user_id=${encodeURIComponent(userId)}`,
            );
            const json = await res.json().catch(() => ({}));
            if (res.ok && Array.isArray(json.owner_ids))
              ownerIds = json.owner_ids;
          } catch (_) {}
          const orParts = [
            `user_id.eq.${userId}`,
            ...sharedUsersOverlapOrClauses(userId),
          ];
          if (ownerIds.length > 0) {
            orParts.push(`user_id.in.(${ownerIds.join(",")})`);
          }
          query = query.or(orParts.join(","));
        } else {
          query = query.eq("user_id", userId);
        }
      }
      // Add sorting by date_ and time_ in descending order
      query = query
        .order("date_", { ascending: false })
        .order("time_", { ascending: false });

      let { data, error, count } = await query;

      if (error) {
        console.error("Error fetching recent audios:", error);
        return;
      }

      // Clean and validate the data
      data = (data || []).map((item) => ({
        ...item,
        time_: item.time_ && typeof item.time_ === "string" ? item.time_ : null,
        date_: item.date_ && typeof item.date_ === "string" ? item.date_ : null,
      }));

      // Filter out items with invalid date or time
      data = data.filter((item) => item.date_ && item.time_);

      // Sort the data
      const sortedData = sortObjectsByDateTime(data, "date_", "time_");
      const hydrated = await hydrateScheduleRowsTimezoneSource(
        supabase,
        sortedData,
      );

      setAllRecentAudios(hydrated);
      setShowAudios(true);
      setTotalPages(Math.ceil((hydrated.length || 0) / itemsPerPage));

      return hydrated;
    } catch (error) {
      console.error("Error fetching recent audios:", error);
      return [];
    }
  };

  useEffect(() => {
    if (allRecentAudios) {
      setRecentAudios(() =>
        allRecentAudios.slice(
          (currentPage - 1) * itemsPerPage,
          currentPage * itemsPerPage,
        ),
      );
    } else {
      getRecentAudios().then((data) => {
        setRecentAudios(
          data.slice(
            (currentPage - 1) * itemsPerPage,
            currentPage * itemsPerPage,
          ),
        );
      });
    }
  }, [newAudioCreated, currentPage, itemsPerPage]);

  const openHTMLInNewTab = (htmlContent) => {
    const newWindow = window.open("", "_blank");
    newWindow.document.write(`
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>HTML Viewer</title>
        </head>
        <body>
          <div id="content">${htmlContent}</div>
        </body>
      </html>
    `);
    newWindow.document.close();
  };

  const handleViewFolderFiles = async (folderPath) => {
    const supabase = await getSupabase();
    const { data, error } = await supabase.storage
      .from(bucketName_files)
      .list(folderPath);

    if (error) {
      console.error("Error fetching files from Supabase:", error);
    } else {
      setCurrentFolderFiles(data);
      setModalIsOpen(true);
    }
  };

  const handleGoUpOneLevel = async () => {
    const supabase = await getSupabase();
    const parentFolderPath = currentFolderPath
      .split("/")
      .slice(0, -1)
      .join("/");

    setPreviousFolderPath(currentFolderPath);
    setCurrentFolderPath(parentFolderPath);
    const { data, error } = await supabase.storage
      .from(bucketName_files)
      .list(parentFolderPath);

    if (error) {
      console.error("Error fetching folders from Supabase:", error);
    } else {
      setCurrentFolderFiles(data);
    }
  };

  const handleGoDownOneLevel = async () => {
    handleViewFolderFiles(previousFolderPath);
  };

  const handleViewReports = async (folderPath) => {
    const supabase = await getSupabase();
    setCurrentFolderPath(folderPath);
    if (!folderPath) {
      setCurrentFolderFiles([]);
      setModalIsOpen(true);
      return;
    }

    const { data, error } = await supabase.storage
      .from(bucketName_files)
      .list(folderPath);

    if (error) {
      console.error("Error fetching files from Supabase:", error);
    } else {
      setCurrentFolderFiles(data);
      setModalIsOpen(true);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (event) => {
    const newItemsPerPage = parseInt(event.target.value, 10);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleCheckboxChange = (id) => {
    // Find the index of the audio item by id
    const index = recentAudios.findIndex((audio) => audio.id === id);

    setSelectedRows((prevSelectedRows) => {
      if (prevSelectedRows[id]) {
        // Deselect the row
        setButtonTexts((prevTexts) =>
          prevTexts.map((text, i) =>
            i === index
              ? recentAudios[index].audio_url
                ? "Listen To Audio"
                : "Record Audio"
              : text,
          ),
        );

        // Remove the id from selectedRows (keeping other selections intact)
        const { [id]: removed, ...remainingSelectedRows } = prevSelectedRows;
        return remainingSelectedRows;
      } else {
        // Select the new row and unselect others
        setButtonTexts((prevTexts) =>
          prevTexts.map((text, i) =>
            i === index
              ? recentAudios[index].audio_url
                ? "Override Audio"
                : "No Show"
              : text,
          ),
        );

        // Only the current id should be selected
        return { [id]: true };
      }
    });
  };

  return (
    <Center className="recent-audios-container" flexDirection="column">
      <div className="recent-audios-container">
        {/* using flex to center text and have button on the right*/}
        <Flex position="relative" align="center" mb={4}>
          <Text mx="auto" textAlign="center" fontSize="2xl" fontWeight="bold">
            Recent Audios
          </Text>

          <Box position="absolute" right="0">
            <Uploader selection={selectedRows} table={recentAudios} />
          </Box>
        </Flex>
        {showAudios &&
          (recentAudios.length > 0 ? (
            <>
              <ResultsTable
                results={recentAudios}
                setResults={setRecentAudios}
                prompts={prompts}
                selectedRows={selectedRows}
                handleCheckboxChange={handleCheckboxChange}
                handleViewJSONFiles={handleViewAudioFiles}
                handleViewFolderFiles={handleViewReports}
                currentFolderPath={currentFolderPath}
                previousFolderPath={previousFolderPath}
                handleGoUpOneLevel={handleGoUpOneLevel}
                handleGoDownOneLevel={handleGoDownOneLevel}
                supabaseUrl={supabaseUrl}
                bucketName_files={bucketName_files}
                modalIsOpen={modalIsOpen}
                setModalIsOpen={setModalIsOpen}
                currentFolderFiles={currentFolderFiles}
                showCheckboxes={true}
                showFiles={true}
                viewerTimezone={viewerTimezone}
              />
              <HStack spacing={4} justify="center" mt={4}>
                <Button
                  onClick={() => handlePageChange(currentPage - 1)}
                  isDisabled={currentPage === 1}
                >
                  Previous
                </Button>
                <Text>
                  Page {currentPage} of {totalPages}
                </Text>
                <Button
                  onClick={() => handlePageChange(currentPage + 1)}
                  isDisabled={currentPage === totalPages}
                >
                  Next
                </Button>
                <Select
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                  width="auto"
                >
                  <option value={5}>5 per page</option>
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                </Select>
              </HStack>
            </>
          ) : (
            <Text align="center" mt={4} fontSize="lg">
              You have no recordings yet, press the record button to get
              started.
            </Text>
          ))}
      </div>
    </Center>
  );
};

export default RecentAudios;
